import type { ChangeEvent, ReactNode } from 'react';
import { LogOut, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';

import { DASHBOARD_KARMA_PILL_ANCHOR } from '@/app/hints';
import { useKarmaTotalQuery } from '@/entities/karma';
import { signOutSession } from '@/entities/session';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import { KarmaCoin, useHintAnchor } from '@/shared/ui/primitives';

interface DashboardHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /**
   * Subtitle slot rendered directly under the brand mark. Kept as a slot so
   * the header stays a presentation shell — the page composes any dynamic
   * data (e.g. queue counts) into it.
   */
  subtitle: ReactNode;
}

/**
 * DashboardHeader — top header with brand mark, search, and controls.
 *
 * The search input and action buttons use a frosted-glass treatment
 * that matches the liquid glass cards throughout the dashboard.
 */
export function DashboardHeader({ searchQuery, onSearchChange, subtitle }: DashboardHeaderProps) {
  const navigate = useNavigate();
  const karmaQuery = useKarmaTotalQuery();
  const totalKarma = karmaQuery.data?.balance ?? 0;
  const karmaPillAnchorRef = useHintAnchor(DASHBOARD_KARMA_PILL_ANCHOR);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.currentTarget.value);
  };

  return (
    <motion.header
      className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <motion.h1
            className="bg-gradient-to-r from-white via-white/60 to-white bg-[length:200%_100%] bg-clip-text text-2xl font-semibold tracking-tight text-transparent drop-shadow-sm"
            animate={{ backgroundPosition: ['200% center', '-200% center'] }}
            transition={{ duration: 4, ease: 'linear', repeat: Infinity, repeatDelay: 1 }}
          >
            Cortex Triage Center
          </motion.h1>
          <button
            ref={karmaPillAnchorRef}
            type="button"
            onClick={() => navigate('/karma')}
            className={cn(
              'flex items-center gap-1.5 rounded-full border border-yellow-400/20 bg-yellow-500/10 px-3 py-1',
              'text-sm font-semibold tabular-nums text-yellow-300',
              'transition-colors hover:border-yellow-400/40 hover:bg-yellow-500/20'
            )}
            aria-label={`${totalKarma} karma points earned`}
          >
            <KarmaCoin size={16} animated={false} />
            {totalKarma} KP
          </button>
        </div>
        {subtitle}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white/60"
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Search patients…"
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search patients"
            className={cn(
              'h-9 w-56 rounded-full border border-white/15 bg-white/8 pl-9 pr-3 text-sm',
              'text-white placeholder:text-white/40 backdrop-blur-xl',
              'outline-none transition-[color,box-shadow,border-color]',
              'focus:border-white/30 focus:ring-[3px] focus:ring-white/10',
              '[&::-webkit-search-cancel-button]:appearance-none',
              '[&::-webkit-search-decoration]:appearance-none',
              'sm:w-64'
            )}
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-white/70 hover:bg-white/10 hover:text-white"
          onClick={() => void signOutSession()}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </motion.header>
  );
}
